import { t, ui, uiError } from '../../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { getWebappApiUrl, webappFetch } from '../../utils/apiConfig';
import {
  IntegrationBlockGroup,
  IntegrationLedgerTransaction,
  PayloadProof,
  categoryColor,
  categoryLabel,
  classifyIntegrationTx,
  formatBytes,
  formatTime,
  indexStatusLabel,
  integrityStatusLabel,
  isValidationOperation,
  ledgerStatusLabel,
  legacyPayload,
  liveBusinessId,
  liveOperation,
  livePayloadSizeBytes,
  liveRecordType,
  operationDisplayLabel,
  payloadProofFromEvidenceError,
  recordStatusLabel,
  shortBusinessId,
  shortHash,
  sourceOrgOf,
  targetOrgOf
} from '../../domain/integrationLedger';

function OperationBadge({ operation }: { operation: string }) {
  if (isValidationOperation(operation)) {
    return <Chip size="small" color="warning" variant="outlined" label={ui(operationDisplayLabel(operation))} title={ui(operation)} />;
  }

  return <Typography variant="body2">{operation || '-'}</Typography>;
}

export function DetailField({
  label,
  value,
  mono = false
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: '#fff' }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {ui(label)}
      </Typography>
      <Typography
        component="div"
        variant="body2"
        sx={{
          mt: 0.5,
          fontFamily: mono ? 'monospace' : 'inherit',
          fontWeight: 600,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word'
        }}
      >
        {value === undefined || value === null || value === '' ? '-' : value}
      </Typography>
    </Paper>
  );
}

export function DashboardMetricCard({
  label,
  value,
  caption,
  color,
  icon,
  gradient,
  highlight = false
}: {
  label: string;
  value: React.ReactNode;
  caption: React.ReactNode;
  color: string;
  icon?: React.ReactNode;
  gradient?: string;
  highlight?: boolean;
}) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2.5,
        height: '100%',
        color: gradient ? '#fff' : 'inherit',
        background: gradient || '#fff',
        transition: 'all 0.25s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        },
        ...(highlight ? {
          animation: 'integrationMetricPulse 4s ease-out',
          '@keyframes integrationMetricPulse': {
            '0%': { boxShadow: `0 0 0 0 ${color}66`, transform: 'translateY(-2px)' },
            '45%': { boxShadow: `0 0 0 7px ${color}22` },
            '100%': { boxShadow: 'none', transform: 'translateY(0)' }
          }
        } : {})
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 'bold', lineHeight: 1.05, mb: 1, overflowWrap: 'anywhere' }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ opacity: gradient ? 0.92 : 0.78, fontWeight: 700 }}>
            {ui(label)}
          </Typography>
          <Typography component="div" variant="caption" sx={{ display: 'block', opacity: gradient ? 0.82 : 0.68, mt: 0.25 }}>
            {caption}
          </Typography>
        </Box>
        {icon && <Box sx={{ display: 'flex', alignItems: 'center', opacity: 0.85, flexShrink: 0 }}>{icon}</Box>}
      </Stack>
    </Paper>
  );
}

export function RawJsonAccordion({ title, data }: { title: string; data: unknown }) {
  const [expanded, setExpanded] = useState(false);
  const [renderedJson, setRenderedJson] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setRenderedJson(null);
    setRendering(false);
  }, [data]);

  useEffect(() => {
    if (!expanded || renderedJson !== null) return;

    setRendering(true);
    let cancelled = false;
    let firstFrame = 0;
    let secondFrame = 0;
    let timeoutId = 0;

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          if (cancelled) return;
          try {
            setRenderedJson(typeof data === 'string' ? data : JSON.stringify(data, null, 2));
          } catch (error) {
            setRenderedJson(`Failed to stringify raw payload: ${error instanceof Error ? error.message : 'unknown error'}`);
          } finally {
            setRendering(false);
          }
        }, 0);
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(timeoutId);
    };
  }, [data, expanded, renderedJson]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
      sx={{ border: '1px solid #e0e0e0', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" fontWeight="bold">{ui(title)}</Typography>
          <Typography variant="caption" color="text.secondary">{t("Raw payload is rendered when this panel is expanded. Large row sets may take a moment.")}</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa', maxHeight: 420, overflow: 'auto' }}>
          {rendering || renderedJson === null ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">{t("Preparing raw payload preview.")}</Typography>
            </Stack>
          ) : (
            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{renderedJson}</pre>
          )}
        </Paper>
      </AccordionDetails>
    </Accordion>
  );
}

export function LedgerStatusChip({ tx }: { tx: IntegrationLedgerTransaction }) {
  const committed = Boolean(tx.block_number && tx.transaction_id);
  return (
    <Chip
      size="small"
      color={committed ? 'success' : 'warning'}
      variant={committed ? 'filled' : 'outlined'}
      label={committed ? t("COMMITTED") : t("PENDING")}
      title={ui(ledgerStatusLabel(tx))}
    />
  );
}

export function IndexStatusChip({ tx }: { tx: IntegrationLedgerTransaction }) {
  const indexed = Boolean(tx.block_number && tx.transaction_id);
  return (
    <Chip
      size="small"
      color={indexed ? 'info' : 'warning'}
      variant="outlined"
      label={indexed ? t("INDEXED") : t("INDEXING")}
      title={ui(indexStatusLabel(tx))}
    />
  );
}

export function IntegrityStatusChip({ tx, proof }: { tx: IntegrationLedgerTransaction; proof?: PayloadProof | null }) {
  const category = classifyIntegrationTx(tx);

  if (category !== 'recordIntegrationResult') {
    return (
      <Chip
        size="small"
        variant="outlined"
        color={category === 'legacyRecordEvent' ? 'default' : 'secondary'}
        label={category === 'legacyRecordEvent' ? t("NO HASH-REF") : t("NOT BUSINESS PROOF")}
        sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
      />
    );
  }

  const matched = proof?.verificationStatus === 'VERIFIED'
    || (!proof?.verificationStatus && proof?.hashMatches === true);
  const mismatched = proof?.verificationStatus === 'MISMATCH'
    || (!proof?.verificationStatus && proof?.hashMatches === false);
  return (
    <Chip
      size="small"
      variant={matched ? 'filled' : 'outlined'}
      color={matched ? 'success' : mismatched ? 'error' : 'warning'}
      label={matched ? t("VERIFIED") : mismatched ? t("MISMATCH") : t("CHECK ON OPEN")}
      title={ui(integrityStatusLabel(tx, proof || undefined))}
    />
  );
}

function payloadRowsCount(payload: Record<string, any> | null | undefined, key: string) {
  const target = payload?.[key];
  if (!target) return undefined;
  if (typeof target.row_count === 'number') return target.row_count;
  if (Array.isArray(target.rows)) return target.rows.length;
  return undefined;
}

export function IntegrationTransactionDetailDialog({
  tx,
  onClose
}: {
  tx: IntegrationLedgerTransaction | null;
  onClose: () => void;
}) {
  const [proof, setProof] = useState<PayloadProof | null>(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofError, setProofError] = useState<string | null>(null);
  const legacy = useMemo(() => tx?.function_name === 'RecordEvent' ? legacyPayload(tx) : null, [tx]);

  useEffect(() => {
    let cancelled = false;
    setProof(null);
    setProofError(null);

    if (!tx || tx.function_name !== 'RecordIntegrationResult') {
      setProofLoading(false);
      return;
    }

    setProofLoading(true);
    webappFetch(`${getWebappApiUrl()}/api/blockchain/transactions/${tx.transaction_id}/integration-payload`)
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok || !body.success) {
          const evidenceError = payloadProofFromEvidenceError(body.code, body.error);
          if (evidenceError) {
            if (!cancelled) {
              setProof(evidenceError);
              setProofError(body.error || 'Payload evidence is unavailable');
            }
            return;
          }
          throw new Error(body.message || body.error || `HTTP ${response.status}`);
        }
        if (!cancelled) {
          setProof({
            hashMatches: body.data.hashMatches,
            evidenceMatches: body.data.evidenceMatches,
            verificationStatus: body.data.verificationStatus,
            verificationSource: body.data.verificationSource,
            topLevelKeys: body.data.rawPayload ? Object.keys(body.data.rawPayload) : [],
            rawPayload: body.data.rawPayload,
            status: body.data.status,
            blockNumber: body.data.blockNumber
          });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setProofError(error instanceof Error ? error.message : 'Failed to load raw payload');
        }
      })
      .finally(() => {
        if (!cancelled) setProofLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tx]);

  const payload = proof?.rawPayload || legacy || null;
  const scheduleRows = payloadRowsCount(payload, 'best_schedule');
  const simLogRows = payloadRowsCount(payload, 'sim_log');
  const movesCount = Array.isArray(payload?.moves) ? payload.moves.length : payload?.summary?.move_count;

  return (
    <Dialog open={Boolean(tx)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("Transaction Detail")}</DialogTitle>
      <DialogContent dividers>
        {tx && (
          <Stack spacing={2}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Category")} value={categoryLabel(tx)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Block")} value={`#${tx.block_number}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Function")} value={tx.function_name || '-'} mono /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Creator MSP")} value={tx.creator_msp_id || '-'} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Tx ID")} value={tx.transaction_id} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Block Hash")} value={tx.block_hash || '-'} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Data Hash")} value={tx.data_hash || '-'} mono /></Grid>
              <Grid item xs={12} sm={6}><DetailField label={t("Previous Hash")} value={tx.previous_hash || '-'} mono /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Sender")} value={sourceOrgOf(tx) || '-'} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Receiver")} value={targetOrgOf(tx) || '-'} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Operation")} value={<OperationBadge operation={liveOperation(tx)} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Chaincode")} value={`${tx.chaincode_name || '-'} ${tx.chaincode_version || ''}`} mono /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Ledger Status")} value={<LedgerStatusChip tx={tx} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Index Status")} value={<IndexStatusChip tx={tx} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Integrity")} value={<IntegrityStatusChip tx={tx} proof={proof || undefined} />} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Business Status")} value={recordStatusLabel(tx)} /></Grid>
            </Grid>

            {tx.function_name === 'RecordIntegrationResult' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("Hash-ref Evidence")}</Typography>
                <Alert severity={proof?.verificationStatus === 'VERIFIED' ? 'success' : proof?.verificationStatus === 'MISMATCH' ? 'error' : 'info'} sx={{ mb: 2 }}>{t("The ledger stores payloadHash and payloadRef instead of the full raw payload. The WebApp reads the committed peer-ledger transaction, follows its payloadRef, recalculates canonical JSON SHA-256 from the PostgreSQL raw payload, and compares the evidence fields.")}</Alert>
                {proofLoading && (
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">{t("Loading raw payload and hash evidence.")}</Typography>
                  </Stack>
                )}
                {proofError && <Alert severity="warning" sx={{ mb: 2 }}>{uiError(proofError)}</Alert>}
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Record Type")} value={tx.record_type || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Business ID")} value={liveBusinessId(tx)} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Payload Size")} value={formatBytes(livePayloadSizeBytes(tx))} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Hash Check")} value={integrityStatusLabel(tx, proof || undefined)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("best_schedule rows")} value={scheduleRows ?? '-'} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("sim_log rows")} value={simLogRows ?? '-'} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("moves count")} value={movesCount ?? '-'} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("Raw Storage Type")} value={tx.payload_storage_type || '-'} mono /></Grid>
                  <Grid item xs={12}><DetailField label={t("Payload Hash")} value={tx.payload_hash || '-'} mono /></Grid>
                  <Grid item xs={12}><DetailField label={t("Payload Ref")} value={tx.payload_ref || '-'} mono /></Grid>
                  <Grid item xs={12}><DetailField label={t("Raw Payload Keys")} value={proof?.topLevelKeys?.join(', ') || '-'} mono /></Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  {payload ? <RawJsonAccordion title={t("Raw Payload")} data={payload} /> : <Typography color="text.secondary">{uiError(proofError || 'Raw payload not loaded yet.')}</Typography>}
                </Box>
              </Paper>
            )}

            {tx.function_name === 'RecordEvent' && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t("Legacy RecordEvent Arguments")}</Typography>
                <Alert severity="info" sx={{ mb: 2 }}>{t("This record uses the legacy `RecordEvent(args[])` format kept for compatibility after chaincode v1.1. It does not include hash-ref payload evidence fields.")}</Alert>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("recordId / args[0]")} value={tx.arg1 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("pipelineId / args[1]")} value={tx.arg2 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("stage / args[2]")} value={tx.arg3 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("prevTxId / args[3]")} value={tx.arg4 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("sender / args[4]")} value={tx.arg5 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("receiver / args[5]")} value={tx.arg6 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("containerRef / args[6]")} value={tx.arg7 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("scenarioRef / args[7]")} value={tx.arg8 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("operationType / args[8]")} value={tx.arg9 || '-'} mono /></Grid>
                  <Grid item xs={12} sm={6} md={3}><DetailField label={t("payload size")} value={formatBytes(livePayloadSizeBytes(tx))} /></Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <RawJsonAccordion title={t("payloadJSON / args[9]")} data={legacy || tx.arg10 || {}} />
                </Box>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function IntegrationBlockDetailDialog({
  block,
  onClose,
  onSelectTx
}: {
  block: IntegrationBlockGroup | null;
  onClose: () => void;
  onSelectTx: (tx: IntegrationLedgerTransaction) => void;
}) {
  const representative = block?.representative || null;
  const sortedRows = (block?.rows || []).slice().sort((a, b) => (a.transaction_index || 0) - (b.transaction_index || 0));
  const totalTxCount = representative?.transaction_count || sortedRows.length;

  return (
    <Dialog open={Boolean(block)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("Block Detail")}</DialogTitle>
      <DialogContent dividers>
        {block && representative && (
          <Stack spacing={2}>
            <Alert severity="info">{t("The admin view lists every indexed transaction in the selected block. Select a row to inspect business details and raw payload evidence.")}</Alert>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Block")} value={`#${block.blockNumber}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Created At")} value={formatTime(representative.block_created_at)} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Transactions in Block")} value={`${totalTxCount}`} /></Grid>
              <Grid item xs={12} sm={6} md={3}><DetailField label={t("Representative Chaincode")} value={`${representative.chaincode_name || '-'} ${representative.chaincode_version || ''}`} mono /></Grid>
              <Grid item xs={12}><DetailField label={t("Block Hash")} value={representative.block_hash || '-'} mono /></Grid>
              <Grid item xs={12}><DetailField label={t("Data Hash")} value={representative.data_hash || '-'} mono /></Grid>
              <Grid item xs={12}><DetailField label={t("Previous Hash")} value={representative.previous_hash || '-'} mono /></Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 42 }}><strong>#</strong></TableCell>
                    <TableCell sx={{ width: 90 }}><strong>{t("Org")}</strong></TableCell>
                    <TableCell sx={{ width: 145 }}><strong>{t("Record Type")}</strong></TableCell>
                    <TableCell><strong>{t("Business ID")}</strong></TableCell>
                    <TableCell sx={{ width: 120 }}><strong>{t("Operation")}</strong></TableCell>
                    <TableCell sx={{ width: 130 }}><strong>{t("Tx ID")}</strong></TableCell>
                    <TableCell sx={{ width: 185 }}><strong>{t("Status")}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((tx) => (
                    <TableRow
                      hover
                      key={tx.transaction_id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => onSelectTx(tx)}
                    >
                      <TableCell>{tx.transaction_index}</TableCell>
                      <TableCell>{sourceOrgOf(tx) || tx.creator_msp_id || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={categoryColor(tx)} label={liveRecordType(tx)} />
                      </TableCell>
                      <TableCell
                        title={liveBusinessId(tx)}
                        sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {shortBusinessId(liveBusinessId(tx))}
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <OperationBadge operation={liveOperation(tx)} />
                      </TableCell>
                      <TableCell title={tx.transaction_id} sx={{ fontFamily: 'monospace' }}>
                        {shortHash(tx.transaction_id, 8, 6)}
                      </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            <LedgerStatusChip tx={tx} />
                          </Stack>
                        </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
